import { useState } from 'react'

import { NavLink, useNavigate } from 'react-router-dom'

import Box from '@mui/material/Box'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'

import {

  NotificationBell,

  NotificationConnectionLostBanner,

} from '@/features/notifications/components'

import { ProviderProfileMenu } from '@/layouts/components/ProviderProfileMenu'

import Typography from '@mui/material/Typography'

import brushfireLogo from '@/layouts/assets/provider-nav-logo.png'

import { useLogout } from '@/features/auth/api'

import { useSpProfile } from '@/features/rewards/api'
import { useSpKycConfiguration } from '@/features/kyc-config'

import { EkycAvatarStatus } from '@/features/ekyc/components'
import { clearEkycHomeSkipped, isEkycUnderReview } from '@/features/ekyc/lib/ekycUi'
import { useEkycPromptStore } from '@/stores/useEkycPromptStore'
import { palettePrimaryMain } from '@/theme/designTokens'

import {

  providerShellNavActionsSx,

  providerShellNavLinkActiveSx,

  providerShellNavLinkInactiveSx,

  providerShellNavLinkIndicatorSlotSx,

  providerShellNavLinkWrapSx,

  providerShellNavLinksSx,

  providerShellMobileNavHeaderSx,

  providerShellMobileNavLinkActiveSx,

  providerShellMobileNavLinkSx,

  providerShellMobileNavSx,

  providerShellNavLogoSx,

  providerShellNavMenuButtonSx,

  providerShellNavRootSx,

} from '@/layouts/styles/providerShellNavStyles'

import { RiCloseLine, RiMenuLine, RiUser3Line } from '@/shared/icons/remix'

import { useSessionStore } from '@/stores/useSessionStore'



const NAV_ITEMS = [

  { label: 'Home', to: '/' },

  { label: 'Rewards', to: '/rewards' },

  { label: 'History', to: '/history' },

  { label: 'Account', to: '/account' },

] as const



function userInitials(name: string | undefined): string | null {

  const parts = name?.trim().split(/\s+/).filter(Boolean) ?? []

  if (parts.length === 0) return null

  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()

  return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase()

}



type NavLinkContentProps = {

  label: string

  isActive: boolean

}



function NavLinkContent({ label, isActive }: Readonly<NavLinkContentProps>) {

  return (

    <Box sx={providerShellNavLinkWrapSx}>

      <Typography

        component="span"

        sx={isActive ? providerShellNavLinkActiveSx : providerShellNavLinkInactiveSx}

      >

        {label}

      </Typography>

      <Box

        sx={{

          ...providerShellNavLinkIndicatorSlotSx,

          bgcolor: isActive ? palettePrimaryMain : 'transparent',

        }}

        aria-hidden

      />

    </Box>

  )

}

type ProviderMobileNavProps = {
  open: boolean
  onClose: () => void
}

function ProviderMobileNav({ open, onClose }: Readonly<ProviderMobileNavProps>) {
  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 200, maxWidth: '70vw' } }}
    >
      <Box
        id="provider-mobile-nav"
        component="nav"
        aria-label="Primary"
        data-testid="provider-nav-drawer"
        sx={providerShellMobileNavSx}
      >
        <Box sx={providerShellMobileNavHeaderSx}>
          <IconButton
            type="button"
            aria-label="Close navigation menu"
            onClick={onClose}
            data-testid="provider-nav-menu-close"
            sx={{ color: '#0F172A' }}
          >
            <RiCloseLine size={22} aria-hidden />
          </IconButton>
        </Box>
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.label}
            to={item.to}
            end={item.to === '/'}
            onClick={onClose}
            data-testid={`provider-mobile-nav-${item.label.toLowerCase()}`}
            style={{ textDecoration: 'none' }}
          >
            {({ isActive }) => (
              <Typography
                component="span"
                sx={isActive ? providerShellMobileNavLinkActiveSx : providerShellMobileNavLinkSx}
              >
                {item.label}
              </Typography>
            )}
          </NavLink>
        ))}
      </Box>
    </Drawer>
  )
}

export function ProviderShellNav() {

  const navigate = useNavigate()

  const userName = useSessionStore((s) => s.user?.name)

  const initials = userInitials(userName)

  const logout = useLogout()

  const { data: profile } = useSpProfile()

  const avatarUrl = profile?.avatarUrl ?? null

  const isVerified = profile?.ekycStatus === 'verified'
  const underReview = isEkycUnderReview(profile?.ekycStatus)
  const { data: effectiveKycMode } = useSpKycConfiguration()
  const showVerifyBanner =
    effectiveKycMode === 'enabled' && !isVerified && !underReview

  const openFromChip = useEkycPromptStore((s) => s.openFromChip)

  const [profileMenuAnchor, setProfileMenuAnchor] = useState<null | HTMLElement>(null)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)

  const profileMenuOpen = Boolean(profileMenuAnchor)



  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {

    setProfileMenuAnchor(event.currentTarget)

  }



  const handleProfileMenuClose = () => {

    setProfileMenuAnchor(null)

  }



  const handleLogout = () => {

    handleProfileMenuClose()

    logout.mutate()

  }



  return (

    <>

    <Box component="header" sx={providerShellNavRootSx} data-testid="provider-shell-nav">

      <IconButton
        type="button"
        aria-label="Open navigation menu"
        aria-controls="provider-mobile-nav"
        aria-expanded={mobileNavOpen}
        aria-haspopup="true"
        onClick={() => setMobileNavOpen(true)}
        data-testid="provider-nav-menu"
        sx={providerShellNavMenuButtonSx}
      >
        <RiMenuLine size={24} aria-hidden />
      </IconButton>

      <NavLink

        to="/"

        end

        aria-label="Go to home"

        data-testid="provider-nav-logo"

        style={{ textDecoration: 'none', display: 'flex', flexShrink: 0 }}

      >

        <Box component="img" src={brushfireLogo} alt="Brushfire" sx={providerShellNavLogoSx} />

      </NavLink>



      <Box component="nav" aria-label="Primary" sx={providerShellNavLinksSx}>

        {NAV_ITEMS.map((item) => (

          <NavLink

            key={item.label}

            to={item.to}

            end={item.to === '/'}

            style={{ textDecoration: 'none' }}

            data-testid={`provider-nav-${item.label.toLowerCase()}`}

          >

            {({ isActive }) => <NavLinkContent label={item.label} isActive={isActive} />}

          </NavLink>

        ))}

      </Box>



      <Box sx={providerShellNavActionsSx}>

        <NotificationBell />



        <EkycAvatarStatus
          status={profile?.ekycStatus}
          onAvatarClick={handleProfileMenuOpen}
          menuOpen={profileMenuOpen}
        >
          {avatarUrl ? (
            <Box
              component="img"
              src={avatarUrl}
              alt=""
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            initials ?? <RiUser3Line size={20} aria-hidden />
          )}
        </EkycAvatarStatus>



        <ProviderProfileMenu
          anchorEl={profileMenuAnchor}
          open={profileMenuOpen}
          onClose={handleProfileMenuClose}
          showVerifyBanner={showVerifyBanner}
          onVerifyNow={() => {
            clearEkycHomeSkipped()
            openFromChip()
          }}
          onProfile={() => {
            handleProfileMenuClose()
            navigate('/account')
          }}
          onSettings={() => {
            handleProfileMenuClose()
            navigate('/account')
          }}
          onLogout={handleLogout}
          logoutDisabled={logout.isPending}
        />

      </Box>

    </Box>

    <ProviderMobileNav open={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />

    <NotificationConnectionLostBanner testId="provider-notification-connection-lost" />

    </>

  )

}

